import { useMemo, useEffect } from 'react';
import { getTestTypeLabel } from 'app/views/synthetics/utils/syntheticsUtils';
import { SynStore, TestModel } from 'app/stores/synthetics/synthTypes';

export interface TestOptionData {
  value: string;
  label: string;
  testType?: string;
  target?: string;
}

interface UseTestOptionsResult {
  options: TestOptionData[] | null;
  isLoading: boolean;
}

/**
 * Hook to fetch and transform test options for use in filter dropdowns.
 * Handles fetching tests from the $syn store and transforming them into
 * options with display info (name, type, target).
 */
export function useTestOptions($syn: SynStore | undefined, enabled: boolean): UseTestOptionsResult {
  const tests = $syn?.tests;
  const isLoading = enabled && tests?.requestStatus === 'fetching';
  const needsFetch = enabled && tests && !tests.hasFetched && !tests.requestStatus;

  // Fetch tests when needed
  useEffect(() => {
    if (needsFetch) {
      tests?.fetch();
    }
  }, [needsFetch, tests]);

  // Transform tests to options with display info
  const options = useMemo(() => {
    if (!enabled || !tests) {
      return null;
    }

    const allTests = tests.get();
    if (!Array.isArray(allTests)) {
      return [];
    }

    return allTests.map((test: TestModel) => ({
      value: String(test.get('id')),
      label: String(test.get('display_name') || test.get('config.name') || `Test ${test.get('id')}`),
      testType: test.get('test_type') as string | undefined,
      target: test.get('config.target.value') as string | undefined
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tests, tests?.hasFetched]);

  return {
    options,
    isLoading: isLoading || needsFetch || false
  };
}

/**
 * Get the label for a test type.
 * Re-exported for convenience in filter components.
 */
export { getTestTypeLabel };
